var m = require('mithril');
var R = require('ramda');
var prop = require('mithril/stream');
var pathViewer = require('../../locations/pathViewer');
var equipmentModel = require('../../equipment/models/equipment');
var smartTableLinksCell = require('./smartTableLinksCell');
var Sortable = require('../../libs/draggableWithDragscroll');
var Dragscroll = require('../../libs/dragscroll');
var io = require('../../libs/socketio');
var loaders = require('../../layout/components/loaders');
var edit = require('../../layout/components/edit');
var modalForm = require('../../layout/components/modalForm');
var elementForm = modalForm(require('../../dashboards/models/element'));
var helpModal = require('../../help/helpModal');
var pointTd = require('../../data/components/pointTd');
var massPointWriteModal = require('../../data/components/massPointWriteModal');
var massPointOverrideModal = require('../../data/components/massPointOverrideModal');
var canWriteColumn = require('../../data/canWriteColumn');
var pagination = require('../../data/components/pagination');
var smartTableSearch = require('./smartTableSearch');
var getEquipmentLocationRid = require('../utils/getEquipmentLocationRid');

const isNotIn = R.curry((array, value) => R.none(R.equals(value), array));
const isDirectDataset = R.propSatisfies(R.lt(R.__, 1), 'componentDepth');
const isChildComponentDataset = R.complement(isDirectDataset);

const DEFAULT_DATASET_NAME = 'Default';
const ROOM_COLUMN_LABEL = 'Rooms Served';

function isDatasetShowInSummary(dataset) {
  if (!dataset) return false;
  if (dataset.showInSummary === true || dataset.showInSummary === 1) return true;
  if (typeof dataset.showInSummary === 'string') {
    return dataset.showInSummary.toLowerCase() === 'true' || dataset.showInSummary === '1';
  }
  return false;
}

function getLinkedRoomLabels(equipmentRecord) {
  return ((equipmentRecord && equipmentRecord.out_containsLocation) || [])
    .map(edge => edge && edge.in)
    .map(locationRecord => {
      if (!locationRecord || locationRecord.roomNumber === undefined || locationRecord.roomNumber === null) return '';
      return String(locationRecord.roomNumber).trim();
    })
    .filter(Boolean);
}

var smartTable = {
  oninit: async function (vnode) {
    var ctrl = this;
    ctrl.element = vnode.attrs.element;
    ctrl.parentReinitialize = vnode.attrs.parentReinitialize;
    ctrl.dashboardCallback = vnode.attrs.dashboardCallback;
    ctrl.equipmentToShow = prop([]);
    ctrl.equipment = prop([]);
    ctrl.showTable = prop(false);
    ctrl.loading = prop(false);
    ctrl.equipmentClassHierarchy = prop([]);
    ctrl.stackedHeaderStructure = prop([]);
    ctrl.headerGrid = prop([]);
    ctrl.redrawHeaderGrid = prop(false);

    //Search Equipment props
    ctrl.showSearchEquipment = prop(false);
    ctrl.searchEquipmentActive = prop(false);
    ctrl.searchEquipmentValue = prop('');
    ctrl.currentPage = prop(1);
    ctrl.totalEquipmentCount = prop(0);
    ctrl.totalEquipmentCountKnown = prop(false);
    ctrl.hasMorePages = prop(false);
    ctrl.searchEquipmentTimer = null;
    ctrl.requestSequence = 0;
    ctrl.getPageSize = function () {
      const configured = Number(ctrl.element().rowLimit);
      return Number.isFinite(configured) && configured > 0 ? configured : 10;
    };
    ctrl.usesServerPagination = function () {
      return !edit.vm.editing();
    };
    ctrl.pageCount = function () {
      if (!ctrl.totalEquipmentCountKnown()) {
        return ctrl.currentPage() + (ctrl.hasMorePages() ? 1 : 0);
      }
      return Math.max(1, Math.ceil(ctrl.totalEquipmentCount() / ctrl.getPageSize()));
    };

    function parseSummaryFields(rawValue) {
      if (!rawValue) return { _enabled: false, _datasets: [], _points: {} };
      if (typeof rawValue === 'object') {
        return {
          _enabled: rawValue._enabled === true,
          _datasets: Array.isArray(rawValue._datasets) ? rawValue._datasets : [],
          _points: rawValue._points && typeof rawValue._points === 'object' ? rawValue._points : {},
        };
      }
      try {
        const parsed = JSON.parse(rawValue);
        return {
          _enabled: parsed && parsed._enabled === true,
          _datasets: Array.isArray(parsed?._datasets) ? parsed._datasets : [],
          _points: parsed && typeof parsed._points === 'object' ? parsed._points : {},
        };
      } catch (_) {
        return { _enabled: false, _datasets: [], _points: {} };
      }
    }

    function buildVisiblePoints(summaryFields) {
      const points = {};
      Object.entries(summaryFields._points || {}).forEach(([key, list]) => {
        if (!Array.isArray(list)) return;
        points[key] = new Set(list);
      });
      return points;
    }

    ctrl.summaryFields = parseSummaryFields(vnode.attrs.element().summaryFields);
    ctrl.visibleDatasets = prop(new Set(ctrl.summaryFields._datasets || []));
    ctrl.visiblePoints = prop(buildVisiblePoints(ctrl.summaryFields));
    ctrl.isSummaryEnabled = function () {
      return ctrl.summaryFields && ctrl.summaryFields._enabled === true;
    };
    ctrl.isDatasetVisible = function (dataset) {
      if (!ctrl.isSummaryEnabled()) return true;
      const key = ctrl.getDatasetKey(dataset);
      return ctrl.visibleDatasets().has(key);
    };
    ctrl.isPointVisible = function (dataset, pointName) {
      if (!ctrl.isSummaryEnabled()) return true;
      const key = ctrl.getDatasetKey(dataset);
      const pointsByDataset = ctrl.visiblePoints();
      if (!Object.prototype.hasOwnProperty.call(pointsByDataset, key)) return true;
      return pointsByDataset[key].has(pointName);
    };
    ctrl.ensureSummaryEnabled = function () {
      if (ctrl.isSummaryEnabled()) return;
      const summaryFields = ctrl.summaryFields || {};
      const summaryDatasets = ctrl.equipmentClassHierarchy().filter(isDatasetShowInSummary);
      summaryFields._enabled = true;
      summaryFields._datasets = summaryDatasets.map(ds => ctrl.getDatasetKey(ds));
      summaryFields._points = {};
      summaryDatasets.forEach(ds => {
        summaryFields._points[ctrl.getDatasetKey(ds)] = ds.points.map(p => p.name);
      });
      ctrl.summaryFields = summaryFields;
      ctrl.visibleDatasets(new Set(summaryFields._datasets));
      ctrl.visiblePoints(buildVisiblePoints(summaryFields));
    };
    ctrl.rowOrder = prop(vnode.attrs.element().rowOrder || []);
    ctrl.canUseManualRowOrder = function () {
      const total = Number(ctrl.totalEquipmentCount());
      return Number.isFinite(total) && total > 0 && total <= ctrl.getPageSize();
    };
    ctrl.hasRoomColumn = function () {
      return Array.isArray(ctrl.equipment()) && ctrl.equipment().some(equipmentRecord => getLinkedRoomLabels(equipmentRecord).length > 0);
    };

    let init = async function (options) {
      options = options || {};
      let data = [];
      const preserveTable = options.preserveTable === true && ctrl.showTable();
      ctrl.loading(true);
      if (!preserveTable) {
        ctrl.showTable(false);
      }
      if (options.resetPage) {
        ctrl.currentPage(1);
      }
      const requestId = ++ctrl.requestSequence;
      const useServerPagination = ctrl.usesServerPagination();
      let params = {};
      const elementRecord = ctrl.element();
      const customEquipment = String(elementRecord.customEquipment || '').trim();
      const smartTableSourceMode = elementRecord.smartTableSourceMode ||
        (customEquipment ? 'customEquipment' : 'classes');
      if (smartTableSourceMode === 'customEquipment') {
        if (customEquipment) {
          params.customEquipment = customEquipment;
        }
      } else if (elementRecord.equipmentClasses && Array.isArray(elementRecord.equipmentClasses) && elementRecord.equipmentClasses.length > 0) {
        params.classes = elementRecord.equipmentClasses.join(',');
      }
      if (elementRecord.customLocations) {
        params.customLocations = elementRecord.customLocations;
      }
      if (elementRecord.rowLimit) {
        params.rowLimit = elementRecord.rowLimit;
      }
      if (elementRecord.componentDepthLimit) {
        params.componentDepthLimit = elementRecord.componentDepthLimit;
      }
      if (edit.vm.editing()) {
        params.summaryOnly = 1;
      }
      if (ctrl.isSummaryEnabled() && !edit.vm.editing()) {
        params.summaryFields = JSON.stringify(ctrl.summaryFields);
      }
      if (useServerPagination) {
        const searchValue = String(ctrl.searchEquipmentValue() || '').trim();
        params.paginatedDetails = 1;
        params.includeTotal = 0;
        params.pageNumber = Math.max(0, ctrl.currentPage() - 1);
        params.pageSize = ctrl.getPageSize();
        if (searchValue.length > 0) {
          params.search = searchValue;
        }
      }
      params.include = 'details';
      var includeChildren = elementRecord.includeChildClasses;
      if (typeof includeChildren === 'undefined' || includeChildren === null) {
        includeChildren = false;
      }
      params.onlyclass = includeChildren ? 0 : 1;

      const equipmentLocationRid = getEquipmentLocationRid(ctrl.element());
      if (!equipmentLocationRid) {
        ctrl.equipment([]);
        ctrl.totalEquipmentCount(0);
        ctrl.showTable(false);
        ctrl.loading(false);
        return;
      }
      const locationRidFragment = equipmentLocationRid.charAt(0) === '#' ? equipmentLocationRid.slice(1) : equipmentLocationRid;

      let fieldDiscovery = null;
      if (edit.vm.editing()) {
        const discoveryParams = {
          ...params,
          locationRid: locationRidFragment,
          summaryOnly: 1
        };
        delete discoveryParams.include;
        delete discoveryParams.rowLimit;
        delete discoveryParams.summaryFields;
        fieldDiscovery = await loaders.requestWithLoader({
          url: '/api/v1/entities/field-discovery',
          method: 'GET',
          params: discoveryParams,
          background: true,
          loader: 'background'
        });
      }

      const searchRequest = {
        url: '/api/v1/entities/search',
        method: 'GET',
        params: {
          ...params,
          locationRid: locationRidFragment
        }
      };
      if (preserveTable) {
        searchRequest.loader = 'background';
      }
      const response = await loaders.requestWithLoader(searchRequest).catch(function (error) {
        if (requestId === ctrl.requestSequence) {
          ctrl.loading(false);
        }
        throw error;
      });
      if (requestId !== ctrl.requestSequence) {
        return;
      }

      if (useServerPagination && response && Array.isArray(response.records)) {
        data = response.records;
        ctrl.totalEquipmentCountKnown(typeof response.total !== 'undefined');
        ctrl.totalEquipmentCount(Number(response.total) || 0);
        ctrl.hasMorePages(response.hasMore === true);
        ctrl.currentPage((Number(response.page) || 0) + 1);
      } else {
        data = Array.isArray(response) ? response : [];
        ctrl.totalEquipmentCountKnown(true);
        ctrl.totalEquipmentCount(data.length);
        ctrl.hasMorePages(false);
      }

      ctrl.equipmentToShow(
        data
      );

      const activeSearch = String(ctrl.searchEquipmentValue() || '').trim().length > 0;
      ctrl.showSearchEquipment(useServerPagination
        ? (ctrl.hasMorePages() || ctrl.totalEquipmentCount() > ctrl.getPageSize() || activeSearch)
        : (data && data.length > 10));
      // Process the paginated data and build the headers
      handleData(data);
      
      // Manual row order is only safe when the full result set fits on this page.
      if (ctrl.canUseManualRowOrder() && ctrl.rowOrder() && ctrl.rowOrder().length) {
        ctrl.equipment(applySavedRowOrder(ctrl.equipment(), ctrl.rowOrder()));
      }

      if (fieldDiscovery) {
        extractClassHierarchyFromDiscovery(fieldDiscovery);
      } else {
        extractClassHierarchy();
      }

      // // Reorder headers if the location has a predefined summary table column order
      if (ctrl.element().location().summaryTableColumns && ctrl.element().location().summaryTableColumns[ctrl.element()['@rid']]) {
        ctrl.sortHeaders(ctrl.element().location().summaryTableColumns[ctrl.element()['@rid']]);
      }
      buildStackedHeaderBlocks();

      buildHeaderGrid();
      ctrl.loading(false);

    }

    ctrl.reinitializeSmartTable = async function (options) {
      await init(options);
      m.redraw();
    };

    ctrl.goToPage = async function (pageNumber) {
      if (ctrl.loading()) return;
      const nextPage = Math.max(1, Math.min(pageNumber, ctrl.pageCount()));
      if (nextPage === ctrl.currentPage()) return;
      ctrl.currentPage(nextPage);
      await ctrl.reinitializeSmartTable({ preserveTable: true });
    };

    ctrl.onSearchEquipmentChange = function (value) {
      ctrl.searchEquipmentValue(value || '');
      if (!ctrl.usesServerPagination()) return;
      if (ctrl.searchEquipmentTimer) {
        clearTimeout(ctrl.searchEquipmentTimer);
      }
      ctrl.searchEquipmentTimer = setTimeout(function () {
        ctrl.reinitializeSmartTable({ resetPage: true, preserveTable: true });
      }, 250);
    };

    ctrl.editHandler = function () {
      ctrl.reinitializeSmartTable();
    };
    edit.on('toggle', ctrl.editHandler);

    ctrl.socketHandler = function (payload) {
      if (!payload) return;
      const elementRid = ctrl.element()['@rid'];
      const payloadElementRid = payload.elementRid;
      if (elementRid && payloadElementRid && elementRid === payloadElementRid) {
        ctrl.reinitializeSmartTable();
        return;
      }
    };

    io.on('comment-event', ctrl.socketHandler);

    function handleData(data) {
      data.forEach(r => {
        if (r['$path'] && Array.isArray(r['$path'])) {
          if (!r['$path'][r['$path'].length - 1].name) {
            r['$path'][r['$path'].length - 1].name = r['$path'][r['$path'].length - 1]['@class'];
          }
        }
      });

      if (data) {
        ctrl.showTable(true);
      }
      ctrl.equipment(data);
    }

    ctrl.getDatasetKey = function (dataset) {
      return `${dataset.datasetName}::${dataset.className}::${dataset.componentDepth}`;
    }


    /**
     * Builds a hierarchical structure of headers for the equipment table.
     *
     * @param {Array<Object>} data - The equipment records data.
     */
    function extractClassHierarchy() {
      // Clear existing headers
      ctrl.equipmentClassHierarchy([]);

      // Process each equipment record to build the hierarchical header structure
      ctrl.equipment().forEach(equipmentRecord => {
        // Process components of the current equipment record
        (equipmentRecord.out_hasComponent || []).forEach(hasComponentEdge => {
          const childEquipmentRecord = hasComponentEdge.in;

          // Skip if child record is invalid or hidden
          if (!childEquipmentRecord) return;

          // Process columns contained within this child record
          processDatasetEdges(childEquipmentRecord['@class'], childEquipmentRecord.out_Contains || [], childEquipmentRecord.name || childEquipmentRecord['@class'], 1);
        });
        // console.log('Processing equipment record:', JSON.parse(JSON.stringify(equipmentRecord)));
        // Process the current equipment record itself
        processDatasetEdges(equipmentRecord['@class'], equipmentRecord.out_Contains || [], equipmentRecord['@class'], 0);
      });
    }

    function extractClassHierarchyFromDiscovery(fieldDiscovery) {
      ctrl.equipmentClassHierarchy([]);
      const datasets = fieldDiscovery && Array.isArray(fieldDiscovery.datasets)
        ? fieldDiscovery.datasets
        : [];
      datasets.forEach(discoveredDataset => {
        const className = discoveredDataset.className;
        const datasetName = discoveredDataset.datasetName || discoveredDataset.name;
        const componentDepth = Number(discoveredDataset.componentDepth) || 0;
        if (!className || !datasetName) return;

        const datasetHeader = findOrCreateDatasetType(className, datasetName, componentDepth);
        datasetHeader.showInSummary = datasetHeader.showInSummary || isDatasetShowInSummary(discoveredDataset);
        const instanceName = discoveredDataset.equipmentName || className;
        if (isChildComponentDataset(datasetHeader) && isNotIn(datasetHeader.instances, instanceName)) {
          datasetHeader.instances.push(instanceName);
        }

        (discoveredDataset.points || []).forEach(point => {
          if (!point || !point.name) return;
          findOrCreatePointType(datasetHeader, point.name, point.columnIndex);
        });
      });
    }

    /**
     * Processes the 'contains' edges of a record, adding relevant datasets and points to the header.
     *
     * @param {Object} equipmentClassHeader - The header record object for the equipment class.
     * @param {Array<Object>} containsEdges - An array of edges representing the containment relationship.
     * @param {string} equipmentName - The name of the equipment associated with the edges.
     */
    function processDatasetEdges(equipmentClassHeader, containsEdges, equipmentName, componentDepth) {
      containsEdges.forEach(containsEdge => {
        const dataset = containsEdge.in;
        const columns = getDisplayColumns(dataset);

        if (columns.length === 0) return;

        // Process each dataset and its associated points
        const datasetHeader = findOrCreateDatasetType(equipmentClassHeader, dataset.name, componentDepth);
        datasetHeader.showInSummary = datasetHeader.showInSummary || isDatasetShowInSummary(dataset);
        // If the dataset belongs to a child component, add the instance name
        if (isNotIn(datasetHeader.instances, equipmentName) && isChildComponentDataset(datasetHeader)) {
          datasetHeader.instances.push(equipmentName);
        }

        processPointEdges(datasetHeader, columns, equipmentName);
      });
    }

    function hasDisplayColumnName(column) {
      return column && column.name !== undefined && column.name !== null && String(column.name).trim().length > 0;
    }

    function getDisplayColumns(dataset) {
      const definitionColumns = Array.isArray(dataset && dataset.columns)
        ? dataset.columns.filter(hasDisplayColumnName)
        : [];
      if (definitionColumns.length > 0) return definitionColumns;

      return ((dataset && dataset.out_Column) || [])
        .map(columnEdge => {
          const edgeName = columnEdge && hasDisplayColumnName(columnEdge) ? columnEdge.name : undefined;
          const pointName = columnEdge && columnEdge.in && hasDisplayColumnName(columnEdge.in) ? columnEdge.in.name : undefined;
          const name = edgeName || pointName;
          return name ? { name, id: columnEdge.index || columnEdge['@rid'] } : null;
        })
        .filter(hasDisplayColumnName);
    }

    /**
     * Finds or creates a dataset header under a given equipment class.
     *
     * @param {string} className - The name of the equipment class.
     * @param {string} datasetName - The name of the dataset to find or create a header for.
     * @param {number} componentDepth - The depth of the component in the hierarchy.
     * @returns {Object} - The header record object for the dataset.
     */
    function findOrCreateDatasetType(className, datasetName, componentDepth) {
      const hierarchy = ctrl.equipmentClassHierarchy();

      // Check if hierarchy is valid
      if (!Array.isArray(hierarchy)) {
        throw new Error('Equipment class hierarchy is not available.');
      }

      // Search criteria for finding existing dataset header
      const includeClassName = isChildComponentDataset({ componentDepth });
      const searchCriteria = {
        datasetName,
        componentDepth,
        ...(includeClassName && { className }) // Keep child component datasets separated by class
      };

      // Find existing dataset header based on search criteria
      let datasetHeader = hierarchy.find(d =>
        Object.keys(searchCriteria).every(key => d[key] === searchCriteria[key])
      );

      // Create new dataset header and add to hierarchy if not found
      if (!datasetHeader) {
        datasetHeader = {
          componentDepth,
          className,
          datasetName,
          instances: [],
          points: [],
          label: getDatasetLabel({ componentDepth, className, datasetName })
        };
        hierarchy.push(datasetHeader);
      }

      return datasetHeader;
    }

    /**
     * Processes the points within a dataset and adds them to the dataset header.
     *
     * @param {Object} datasetHeader - The header record object for the dataset.
     * @param {Array<Object>} columns - An array of columns objects within the dataset.
     * @param {string} instanceName - The name of the equipment associated with the points.
     */
    function processPointEdges(datasetHeader, columns, instanceName) {
      columns.forEach(column => {
        // Check by name or ID
        const exists = datasetHeader.points.some(p => p.name === column.name);
        if (!exists) {
          datasetHeader.points.push({ name: column.name, instances: [], index: column.id });
        }
      });
    }

    /**
     * Finds or creates a point header under a given dataset header.
     *
     * @param {Object} datasetHeader - The header record object for the dataset.
     * @param {string} columnName - The name of the point to find or create a header for.
     * @returns {Object} - The header record object for the point.
     */
    function findOrCreatePointType(datasetHeader, columnName, columnId) {
      let pointHeader = datasetHeader.points.find(p => p.name === columnName);

      if (!pointHeader) {
        pointHeader = { name: columnName, instances: [], index: columnId };
        datasetHeader.points.push(pointHeader);
      }

      return pointHeader;
    }


    function getDatasetLabel(dataset) {
      if (isDirectDataset(dataset)) {
        return dataset.datasetName;
      } else {
        return dataset.datasetName === DEFAULT_DATASET_NAME ? dataset.className : `${dataset.className} - ${dataset.datasetName}`;
      }
    }
    /**
     * Builds the stacked header structure for visualization by processing each dataset in the class hierarchy.
     *
     * Iterates over the equipment class hierarchy, generates a header structure for each dataset, and updates
     * the global stacked header structure.
     */
    function getInstanceLabelMode() {
      const raw = ctrl.element && typeof ctrl.element === 'function' ? ctrl.element().instanceLabelMode : null;
      return raw === 'full' ? 'full' : 'suffix';
    }

    function buildStackedHeaderBlocks(){
      const labelMode = getInstanceLabelMode();
      ctrl.equipmentClassHierarchy().forEach(ds => {
        if (isChildComponentDataset(ds) && ds.instances.length > 0) {
          if (labelMode === 'suffix') {
            ds.instances = [
              ...new Set(
                ds.instances.map(instName => {
                  const safeName = typeof instName === 'string' ? instName : String(instName || '');
                  if (!safeName) return safeName;
                  // split on dash or space, take last segment
                  const parts = safeName.split(/[- ]/);
                  return parts[parts.length - 1];
                })
              )
            ];
          } else {
            ds.instances = [
              ...new Set(
                ds.instances.map(instName => (typeof instName === 'string' ? instName : String(instName || '')))
              )
            ];
          }
        }
      });
      const isEditing = edit.vm.editing();
      const visiblePoints = ctrl.visiblePoints();
      const allowListEnabled = ctrl.isSummaryEnabled();
      const datasets = ctrl.equipmentClassHierarchy()
        .filter(ds => isEditing || !allowListEnabled || ctrl.isDatasetVisible(ds))
        .map(ds => {
          if (!isEditing && allowListEnabled) {
            const key = ctrl.getDatasetKey(ds);
            if (Object.prototype.hasOwnProperty.call(visiblePoints, key)) {
              ds = {
                ...ds,
                points: ds.points.filter(p => visiblePoints[key].has(p.name)),
              };
            }
          }
          return ds;
        });

      ctrl.stackedHeaderStructure( datasets.map(buildHeaderBlock) );
    }

    function getInstanceLabel(instanceName) {
      const safeName = typeof instanceName === 'string' ? instanceName : String(instanceName || '');
      return safeName;
    }

    /**
     * Creates a stacked header structure from a dataset record with consideration for componentDepth.
     *
     * @param {Object} dataset - The dataset record containing className, label, instances, points, and componentDepth.
     * @returns {Array<Array<string>>} - A 2D array representing the stacked header.
     */
    function buildHeaderBlock(dataset) {
      const block = [];

      if (edit.vm.editing()) {
        const combinedLabelRow = [getDatasetLabel(dataset)];
        block.push(combinedLabelRow);
        block.push([]);
        block.push([]);
        return { dataset, block };
      }

      // Case 1: If dataset is direct child of this entity and dataset name is DEFAULT_DATASET_NAME, push a single row (point names)
      if (isDirectDataset(dataset) && dataset.datasetName === DEFAULT_DATASET_NAME) {
        const datasetLabelRow = [getDatasetLabel(dataset)];  // Single cell for the dataset label
        const pointsRow = dataset.points.map(point => point.name);
        block.push([]);
        block.push(datasetLabelRow);
        block.push(pointsRow);
      }
      // Case 2: If dataset is direct child of this entity and dataset name is not DEFAULT_DATASET_NAME, push two rows (dataset label, points)
      else if (isDirectDataset(dataset) && dataset.datasetName !== DEFAULT_DATASET_NAME) {
        const datasetLabelRow = [getDatasetLabel(dataset)];  // Single cell for the dataset label
        const pointsRow = dataset.points.map(point => point.name);
        block.push([])
        block.push(datasetLabelRow);
        block.push(pointsRow);
      }
      // Case 3: If dataset belongs to a child component and only one instance, push two rows (combined label, points)
      else if (isChildComponentDataset(dataset) && dataset.instances.length === 1) {
        let label = [getDatasetLabel(dataset)];
        const combinedLabelRow = [label];
        const pointsRow = dataset.points.map(point => point.name);
        block.push([])
        block.push(combinedLabelRow);
        block.push(pointsRow);
      }
      // Case 4: If dataset belongs to a child component and there are multiple instances, push three rows (combined label, points, instances)
      else if (isChildComponentDataset(dataset) && dataset.instances.length > 1) {
        let label = [getDatasetLabel(dataset)];
        const combinedLabelRow = [label];
        const pointsRow = dataset.points.map(point => point.name);

        // Sort instance names alphabetically
        dataset.instances.sort((a, b) => a.localeCompare(b));

        // Repeat instances for each point
        const instancesRow = [];
        dataset.points.forEach(() => {
          instancesRow.push(...dataset.instances.map(inst => getInstanceLabel(inst)));
        });

        block.push(combinedLabelRow);
        block.push(pointsRow);
        block.push(instancesRow);
      }

      return { dataset, block };
    }

    function buildHeaderGrid() {
      // Get the array of header blocks
      const headerBlocks = ctrl.stackedHeaderStructure().map(({ dataset, block }) => block);
      // Determine the maximum depth across all header blocks
      const maxDepth = headerBlocks.reduce((max, block) => Math.max(max, block.length), 0);
      // Initialize the header grid, an array for each row
      const headerGrid = Array.from({ length: maxDepth }, () => []);
      // Build the header grid by traversing all header blocks
      for (let rowIndex = 0; rowIndex < maxDepth; rowIndex++) {
        const isBottomRow = rowIndex === maxDepth - 1;

        // Equipment name column
        headerGrid[rowIndex].push({
          content: '',
          colspan: 1,
          rowspan: 1,
        });

        // Room/Zone column
        if (ctrl.hasRoomColumn()) {
          headerGrid[rowIndex].push({
            content: isBottomRow ? ROOM_COLUMN_LABEL : '',
            colspan: 1,
            rowspan: 1,
          });
        }

        headerBlocks.forEach((block, blockIndex) => {
          const numColumnsInBlock = getTotalColumns(block);
          const dataset = ctrl.stackedHeaderStructure()[blockIndex].dataset;
          const row = block[rowIndex];
          if (row.length === 0) {
            // Add an empty cell that spans the block's columns
            headerGrid[rowIndex].push({
              content: '',
              colspan: numColumnsInBlock,
              rowspan: 1,
            });
          } else {
            row.forEach((cellContent, cellIndex) => {
              const colspan = calculateColspan(block, rowIndex, cellIndex);
              const rowspan = calculateRowspan(block, rowIndex, cellIndex, maxDepth);

              headerGrid[rowIndex].push({
                content: cellContent,
                colspan: colspan > 1 ? colspan : undefined,
                rowspan: rowspan > 1 ? rowspan : undefined,
              });
            });
          }
        });
      }
      ctrl.headerGrid(headerGrid);
    }


    /**
     * Helper function to get the total number of columns represented by a header block
     */
    function getTotalColumns(block) {
      const numPoints = block[1] ? block[1].length : 1;
      const numInstancesPerPoint = block[2] ? block[2].length / numPoints : 1;
      return numPoints * numInstancesPerPoint;
    }

    function calculateColspan(block, rowIndex, cellIndex) {
      if (rowIndex === 0) {
        // First row (dataset label)
        return getTotalColumns(block);
      } else if (rowIndex === 1) {
        // Second row (point names)
        const numInstances = block[2] ? block[2].length : 1;
        const numPoints = block[1].length;
        const instancesPerPoint = Math.floor(numInstances / numPoints) || 1;
        // Handle any remaining instances
        if (cellIndex === numPoints - 1) {
          // Add any remaining instances to the last point
          const remainder = numInstances % numPoints;
          return instancesPerPoint + remainder;
        }
        return instancesPerPoint;
      } else {
        return 1;
      }
    }

    function calculateRowspan(block, rowIndex, cellIndex, maxDepth) {
      if (rowIndex === 0 && block.length < maxDepth) {
        // Extend the first cell's rowspan if the block is shorter than maxDepth
        return maxDepth - block.length + 1;
      } else {
        return 1;
      }
    }


    /**
     * Renders the location link in the first cell of the third header row.
     *
     * @returns {VNode} - The virtual DOM node representing the location link.
     */
    ctrl.renderLocationLink = function renderLocationLink() {
      return m('div.f1.fw1', [
        true === undefined ?
          vnode.attrs.element().location()['@class'] === 'Dashboard' ?
            `Home`
            :
            renderBreadcrumb()
          :
          true === true ?
            m(m.route.Link, { class: 'mr1', href: getLocationHref() }, getLocationName())
            :
            `${ctrl.equipmentClass.replace(/([A-Z]+)/g, " $1").replace(/([A-Z][a-z])/g, " $1")}s`
      ]);
    }

    /**
     * Renders the breadcrumb navigation for the location.
     *
     * @returns {VNode} - The virtual DOM node representing the breadcrumb navigation.
     */
    function renderBreadcrumb() {
      return m('div.flex.flex-wrap.blue', [
        m(m.route.Link, { class: 'pr1 truncate', href: getLocationHref() }, getLocationName()),
        m('div.flex.items-center.f3', [
          '( ',
          m(m.route.Link, { class: 'mr1 truncate', href: getParentLocationHref() }, vnode.attrs.element().location().parent.name),
          ' )'
        ])
      ]);
    }

    /**
     * Gets the current location's name or "root" if not defined.
     *
     * @returns {string} - The name of the current location.
     */
    function getLocationName() {
      return vnode.attrs.element().location().name === 'root' ? 'Home' : vnode.attrs.element().location().name;
    }

    /**
     * Constructs the href for the current location.
     *
     * @returns {string} - The href for the current location.
     */
    function getLocationHref() {
      return `/location/${vnode.attrs.element().location()['@rid'].slice(1)}${m.route.get().split('?')[1] ? `?${m.route.get().split('?')[1]}` : ''}`;
    }


    /**
     * Constructs the href for the parent location.
     *
     * @returns {string} - The href for the parent location.
     */
    function getParentLocationHref() {
      return `/location/${vnode.attrs.element().location().parent['@rid'].slice(1)}${m.route.get().split('?')[1] ? `?${m.route.get().split('?')[1]}` : ''}`;
    }

    ctrl.reorderHeaders = async function (columnHeaders) {
      const rid = ctrl.element()['@rid'];
      const currentSummary = ctrl.element().location().summaryTableColumns || {};
      const rawData = currentSummary[rid];
    
      const isLegacyArray = Array.isArray(rawData); // e.g. ["Default", "Airflow"]
      const isLegacyObject = typeof rawData === 'object' && !rawData._order;
      const isNewFormat = typeof rawData === 'object' && Array.isArray(rawData._order);
    
      // Build new body that will replace the old one
      const body = { ...currentSummary };
      body[rid] = { _order: [] };
    
      // Build dataset lookup by both composite and legacy keys
      const datasetMap = ctrl.equipmentClassHierarchy().reduce((acc, dataset) => {
        const key = ctrl.getDatasetKey(dataset);
        const label = getDatasetLabel(dataset);
        acc[key] = dataset;                 // composite key
        acc[dataset.datasetName] = dataset; // legacy by dataset name
        acc[dataset.className] = dataset;   // label for child default datasets
        acc[label] = dataset;               // exact label text used in the header grid
        return acc;
      }, {});
    
      // Convert header.id (from Sortable) to correct composite key
      body[rid]._order = columnHeaders.map(header => {
        const match = datasetMap[header.id];
        return match ? ctrl.getDatasetKey(match) : header.id;
      });
    
      // Save point order using composite keys
      ctrl.equipmentClassHierarchy().forEach(dataset => {
        const key = ctrl.getDatasetKey(dataset);
        body[rid][key] = dataset.points.map(p => p.name);
      });
    
      // Reapply sorting locally
      ctrl.sortHeaders(body[rid]);
    
      buildStackedHeaderBlocks();
      buildHeaderGrid();
      ctrl.redrawHeaderGrid(true);
      m.redraw();
    
      await loaders.requestWithLoader({
        method: 'PUT',
        url: `/api/v1/location/${ctrl.element().location()['@rid'].slice(1)}/summaryTableColumns`,
        body,
      });
    
      ctrl.element().location().summaryTableColumns = body;
    };

    ctrl.reorderPoints = async function (datasetHeader, newPointOrder) {
      const rid = ctrl.element()['@rid'];
      const currentSummary = ctrl.element().location().summaryTableColumns || {};
      const rawData = currentSummary[rid];

      // Initialize with new format if missing or legacy
      const body = { ...currentSummary };
      if (!body[rid] || Array.isArray(body[rid])) {
        body[rid] = { _order: [] };
      }

      // Use composite key for saving
      const key = ctrl.getDatasetKey(datasetHeader);

      body[rid][key] = newPointOrder.map(pointDiv => pointDiv.id);

      // Update dataset points in memory (reorder points)
      datasetHeader.points = newPointOrder.map(pointName =>
        datasetHeader.points.find(p => p.name === pointName.id)
      );

      // Save via API
      await loaders.requestWithLoader({
        method: 'PUT',
        url: `/api/v1/location/${ctrl.element().location()['@rid'].slice(1)}/summaryTableColumns`,
        body,
      });

      // Update local copy
      ctrl.element().location().summaryTableColumns = body;

      ctrl.redrawHeaderGrid(true);
      m.redraw();
    };

    ctrl.sortHeaders = function (summaryData) {
      let order = [];
      const isNewFormat = summaryData && summaryData._order && Array.isArray(summaryData._order);
    
      if (isNewFormat) {
        order = summaryData._order;
      } else if (Array.isArray(summaryData)) {
        order = summaryData;
      }
    
      const orderMap = order.reduce((acc, name, index) => {
        acc[name] = index;
        return acc;
      }, {});
      
      const highIndex = order.length;
      
      ctrl.equipmentClassHierarchy().sort((a, b) => {
        const keyA = ctrl.getDatasetKey(a);
        const keyB = ctrl.getDatasetKey(b);
        const labelA = getDatasetLabel(a);
        const labelB = getDatasetLabel(b);
        const indexA = orderMap[keyA]
          ?? orderMap[a.datasetName]
          ?? orderMap[a.className]
          ?? orderMap[labelA]
          ?? highIndex;
        const indexB = orderMap[keyB]
          ?? orderMap[b.datasetName]
          ?? orderMap[b.className]
          ?? orderMap[labelB]
          ?? highIndex;
        return indexA - indexB;
      });
    
      
      // Sort points if available
      ctrl.equipmentClassHierarchy().forEach(dataset => {
        if (isNewFormat) {
          const key = ctrl.getDatasetKey(dataset);
          const legacyKey = dataset.datasetName;
          const pointOrder = summaryData[key] || summaryData[legacyKey];
          if (pointOrder) {
            dataset.points.sort((a, b) => {
              const indexA = pointOrder.indexOf(a.name);
              const indexB = pointOrder.indexOf(b.name);
              return (indexA !== -1 ? indexA : pointOrder.length) - (indexB !== -1 ? indexB : pointOrder.length);
            });
          }
        }
      });
    };

    ctrl.sortInstanceNamesAlphabetically = function (columns) {
      columns.forEach(column => {
        column.instances.sort((a, b) => a.localeCompare(b));
      });
    }

    async function saveSummaryConfig() {
      const element = vnode.attrs.element();
      const rid = element['@rid'];
      const summaryFields = ctrl.summaryFields || { _enabled: true, _datasets: [], _points: {} };
      const availablePoints = {};
      ctrl.equipmentClassHierarchy().filter(isDatasetShowInSummary).forEach(dataset => {
        const key = ctrl.getDatasetKey(dataset);
        availablePoints[key] = new Set((dataset.points || []).map(point => point.name).filter(Boolean));
      });
      summaryFields._enabled = true;
      summaryFields._datasets = Array.from(ctrl.visibleDatasets()).filter(key => availablePoints[key]);
      const allowedSet = new Set(summaryFields._datasets);
      summaryFields._points = Object.fromEntries(
        summaryFields._datasets
          .map(key => [key, ctrl.visiblePoints()[key] || availablePoints[key]])
          .filter(([key]) => allowedSet.has(key))
          .map(([key, set]) => [key, Array.from(set).filter(pointName => availablePoints[key] && availablePoints[key].has(pointName))])
      );
    
      await loaders.requestWithLoader({
        method: 'PUT',
        url: `/api/v1/element/${rid.slice(1)}`,
        body: { summaryFields: summaryFields }
      });

      element.summaryFields = summaryFields;
      ctrl.summaryFields = summaryFields;
    }
    

    // Methods to toggle hide state and persist config
    ctrl.toggleHideHeader = async function(dataset) {
      if (!isDatasetShowInSummary(dataset)) return;
      ctrl.ensureSummaryEnabled();
      const key = ctrl.getDatasetKey(dataset);
      const visible = new Set(ctrl.visibleDatasets());
      if (visible.has(key)) visible.delete(key);
      else visible.add(key);
      ctrl.visibleDatasets(visible);
      await saveSummaryConfig();
      init();
    };

    ctrl.toggleHidePoint = async function(dataset, point) {
      if (!isDatasetShowInSummary(dataset)) return;
      ctrl.ensureSummaryEnabled();
      const key = ctrl.getDatasetKey(dataset);
      const pointsByDataset = Object.assign({}, ctrl.visiblePoints());
      if (!pointsByDataset[key]) {
        pointsByDataset[key] = new Set(dataset.points.map(p => p.name));
      }
      if (pointsByDataset[key].has(point)) pointsByDataset[key].delete(point);
      else pointsByDataset[key].add(point);
      ctrl.visiblePoints(pointsByDataset);
      await saveSummaryConfig();
      init();
    };


    // Apply saved order (keeps unknown/missing rids at the end)
    function applySavedRowOrder(list, savedOrder) {
      if (!Array.isArray(list) || !Array.isArray(savedOrder) || savedOrder.length === 0) return list;
      const byId = new Map(list.map(r => [r['@rid'], r]));
      const ordered = savedOrder.map(id => byId.get(id)).filter(Boolean);
      // append any not listed yet (new rows)
      const remaining = list.filter(r => !savedOrder.includes(r['@rid']));
      return [...ordered, ...remaining];
    }

    // persist rowOrder on the element
    async function saveRowOrder(newOrder) {
      const elementRid = ctrl.element()['@rid'];
      const body = { rowOrder: newOrder };
      await loaders.requestWithLoader({
        method: 'PUT',
        url: `/api/v1/element/${elementRid.slice(1)}`,
        body
      });
      // keep local copies in sync
      ctrl.rowOrder(newOrder);
      vnode.attrs.element().rowOrder = newOrder;
    }

    ctrl.reorderRows = async function(newOrderNodes) {
      // newOrderNodes is the array of <tr> DOM nodes Sortable returns (like your other onsort handlers)
      const newOrder = newOrderNodes.map(n => n.id); // our <tr id={@rid}>
      
      // Reorder in-memory list
      const byId = new Map(ctrl.equipment().map(r => [r['@rid'], r]));
      const reordered = newOrder.map(id => byId.get(id)).filter(Boolean);
      const leftovers = ctrl.equipment().filter(r => !byId.has(r['@rid']) || !newOrder.includes(r['@rid']));
      ctrl.equipment([...reordered, ...leftovers]);
    
      await saveRowOrder(newOrder);
      m.redraw();
    };

    await init();

  },
  onupdate: function(vnode) {
    var ctrl = this;

  },
  onremove: function () {
    if (this.searchEquipmentTimer) {
      clearTimeout(this.searchEquipmentTimer);
    }
    if (this.socketHandler) {
      io.off('comment-event', this.socketHandler);
    }
    if (this.editHandler) {
      edit.removeListener('toggle', this.editHandler);
    }
  },
  view: function (vnode) {
    const ctrl = this;

    // While the equipment list is loading, render a stable placeholder
    if (!ctrl.showTable()) {
      const rows = Number(ctrl.element().rowLimit) || 10;
      const rowHeightPx = 22;
      const headerHeightPx = 40;
      const minHeight = headerHeightPx + rows * rowHeightPx;
      const cols = 7; // base columns + 5 value columns default
      const headerCells = [];
      const title = ctrl.element() && ctrl.element().name ? String(ctrl.element().name) : '';
      const firstColChWidth = Math.max(8, Math.min(40, title.length + 2));
      const otherColWidth = Math.max(10, Math.floor((100 - 5) / (cols - 1)));
      headerCells.push(
        m('th.tl.bb.b--mid-gray.pr3', { style: { width: firstColChWidth + 'ch', whiteSpace: 'nowrap' } }, [
          m('div.flex.items-end.f1.fw5', [
            m('span', title)
          ])
        ])
      );
      for (let c = 1; c < cols; c++) {
        headerCells.push(
          m('th.tc.bb.b--mid-gray', { style: { width: otherColWidth + '%' } }, [
            m('div.br2.bg-dark-gray.o-30.center.skeleton', { style: { height: '12px', width: '50%' } })
          ])
        );
      }
      const bodyRows = [];
      for (let r = 0; r < rows; r++) {
        const cells = [];
        cells.push(
          m('td.bb.moon-gray.tl.nowrap.pr3.overflow-hidden', { style: { width: firstColChWidth + 'ch', whiteSpace: 'nowrap' } }, [
            m('div.f4.br2.bg-dark-gray.o-20.skeleton', { style: { height: '1em', width: '70%' } })
          ])
        );
        for (let c = 1; c < cols; c++) {
          const w = 70 - ((r + c) % 3) * 10;
          cells.push(
            m('td.bb.bl-0', { style: { width: otherColWidth + '%' } }, [
              m('div.f4.br2.bg-dark-gray.o-20.center.skeleton', { style: { height: '1em', width: w + '%' } })
            ])
          );
        }
        bodyRows.push(m('tr', cells));
      }
      
      return m('div', {
        id: 'smart-table-container-skeleton-' + vnode.attrs.element()['@rid'],
        'aria-busy': 'true',
        'aria-label': 'Loading Smart Table'
      }, [
        m('div.cf.overflow-hidden', [
          m('div.dragscroll-container', [
            m('div.dragscroll', { oncreate: Dragscroll, onupdate: Dragscroll }, [
              m('table.fw2.f3.pr4.pb3.narrow', { style: { tableLayout: 'fixed', width: '100%', minWidth: Math.max(540, (cols + 1) * 120) + 'px', minHeight: minHeight + 'px' } }, [
                m('thead', [ m('tr', headerCells) ]),
                m('tbody', bodyRows)
              ])
            ])
          ])
        ])
      ]);
    }

    function renderHeader () {
      if (ctrl.headerGrid().length === 0) {
        ctrl.headerGrid([[{}], [{}], [{}]]);
      }
    
      const lastRow = ctrl.headerGrid().length - 1;
      const leafDescriptors = getVisibleLeafColumnDescriptors();
      const leafColumnOffset = 1 + (ctrl.hasRoomColumn() ? 1 : 0);
    
      return m('thead', ctrl.headerGrid().map((row, rowIdx) => {
        // --- Pre-calculate section boundaries for the last row ---
        let leftBoundaryIdxs = new Set();
        let rightBoundaryIdxs = new Set();
    
        if (rowIdx === lastRow && lastRow > 0) {
          const prevRow = ctrl.headerGrid()[rowIdx - 1];
          let currentIndex = 0;
          prevRow.forEach((prevCell, pIdx) => {
            const span = prevCell.colspan || 1;
            if (pIdx !== 0) {
              const left = currentIndex;
              const right = currentIndex + span - 1;
              leftBoundaryIdxs.add(left);
              rightBoundaryIdxs.add(right);
            }
            currentIndex += span;
          });
        }
    
        return m('tr', row.map((cell, cellIdx) => {
          const attrs = {};
          if (rowIdx === 0) {
            const contentKey = cell.content != null ? String(cell.content) : '';
            attrs.key = `r${rowIdx}-c${cellIdx}-${contentKey}`;
            if (cell.content != null) {
              attrs.id = String(cell.content);
            }
          }
    
          // Dynamic content
          if (rowIdx === 1 && cellIdx === 0 && ctrl.showSearchEquipment()) {
            cell.content = m(smartTableSearch, {
              searchEquipmentActive: ctrl.searchEquipmentActive,
              searchEquipmentValue: ctrl.searchEquipmentValue,
              onSearchChange: ctrl.onSearchEquipmentChange,
              elementRid: vnode.attrs.element()['@rid'],
              title: vnode.attrs.element().name
            });
          }
          if (rowIdx === 2 && cellIdx === 0) {
            cell.content = m('div.flex.items-end.f1.fw5', {
              title: vnode.attrs.element().name
            }, [
              m('div' + (hasNameColumnWidth(ctrl.element()) ? '.smart-table-title-content' : '.nowrap'), vnode.attrs.element().name),
              vnode.attrs.element().helpText
                ? m('i.ion-ios-information-outline.white.f4.ml2.mr2.flex-none', {
                    onclick: () => {
                      helpModal.editDialogue(
                        vnode.attrs.element().name,
                        undefined,
                        vnode.attrs.element().helpText
                      );
                    }
                  })
                : ''
            ]);
          }
    
          if (cell.rowspan && cell.rowspan > 1) attrs.rowspan = cell.rowspan;
          if (cell.colspan && cell.colspan > 1) attrs.colspan = cell.colspan;

          const classes = ['fw2', 'grid-table', 'ph1'];
          const leafDescriptor = rowIdx === lastRow && cellIdx >= leafColumnOffset
            ? leafDescriptors[cellIdx - leafColumnOffset]
            : null;
          const canOpenMassOverride = leafDescriptor && descriptorHasOverridePoint(leafDescriptor);
          const canOpenMassWrite = leafDescriptor && descriptorHasWritablePoint(leafDescriptor);
          if (cellIdx === 1 && ctrl.hasRoomColumn()) {
            attrs.style = Object.assign({ width: 'auto' }, attrs.style);
          }

          // First column (element name)
          if (cellIdx === 0) {
            classes.push('tl', 'v-btm', 'bw1');
            if (hasNameColumnWidth(ctrl.element())) classes.push('smart-table-name-column');
          } else {
            classes.push('tc', 'bw1');
          }
    
          if (rowIdx === 0) classes.push('v-btm');
          if (rowIdx === lastRow) {
            classes.push('bb', 'v-btm');
          }
    
          // Border rules
          if (cellIdx !== 0) {
            if (rowIdx === 0 || rowIdx === 1) {
              classes.push('bl', 'br');
            } else if (rowIdx === lastRow) {
              if (leftBoundaryIdxs.has(cellIdx)) classes.push('bl');
              if (rightBoundaryIdxs.has(cellIdx)) classes.push('br');
            }
          }
    
          attrs.class = classes.join(' ');
          if (canOpenMassOverride || canOpenMassWrite) {
            attrs.class += ' pointer underline';
            attrs.title = canOpenMassOverride ? massOverrideTitle(leafDescriptor) : massWriteTitle(leafDescriptor);
            attrs.onclick = function (evt) {
              evt.preventDefault();
              evt.stopPropagation();
              if (canOpenMassOverride) {
                openMassOverrideModal(leafDescriptor);
              } else {
                openMassWriteModal(leafDescriptor);
              }
            };
          }
          return m('th', attrs, cell.content === 'Default' ? '' : cell.content);
        }));
      }));
    }
    function renderEditModeHeader() {
      if (ctrl.headerGrid().length === 0) {
        ctrl.headerGrid([[{}], [{}], [{}]]);
      }
      return m('thead',
        ctrl.headerGrid().map((row, rowIdx) => {
          if (rowIdx === 0) {
            return m(Sortable, {
                onsort: ctrl.reorderHeaders,
                draggable: '.grid-table',
                handleClass: '.grid-table-drag-handle',
                htmlElement: 'tr',
              },
              row.map((cell, cellIdx) => {
                const attrs = {};
                const contentKey = cell.content != null ? String(cell.content) : '';
                attrs.key = `r${rowIdx}-c${cellIdx}-${contentKey}`;
                if (cell.content != null) {
                  attrs.id = String(cell.content);
                }
                if (cell.rowspan && cell.rowspan > 1) attrs.rowspan = cell.rowspan;
                if (cell.colspan && cell.colspan > 1) attrs.colspan = cell.colspan;
                if (cellIdx === 1 && ctrl.hasRoomColumn()) {
                  attrs.style = Object.assign({ width: 'auto' }, attrs.style);
                }

                if (cellIdx === 0 || (ctrl.hasRoomColumn() && cellIdx === 1)) {
                  attrs.class = 'tl v-btm fw2 bw1 ph1 nowrap' + (hasNameColumnWidth(ctrl.element()) ? ' smart-table-name-column' : '');
                  return m('th', attrs, cell.content);
                } else if (rowIdx === 0) {
                  const hasRoomCol = ctrl.hasRoomColumn();
                  const offset    = 1 + (hasRoomCol ? 1 : 0);
                  const dsIndex   = cellIdx - offset;                   // index into ctrl.stackedHeaderStructure
                  const dataset   = ctrl.stackedHeaderStructure()[dsIndex].dataset;
                  const key       = ctrl.getDatasetKey(dataset);
                  const summaryEligible = isDatasetShowInSummary(dataset);
                  const hidden    = !summaryEligible || (ctrl.isSummaryEnabled() && !ctrl.isDatasetVisible(dataset));

                  attrs.class = 'tl v-btm fw2 bw1 br nowrap grid-table';
                  return m('th', 
                    attrs, 
                    [
                      m('div.grid-table-drag-handle.flex.justify-between', [
                        cell.content,
                        m('div.ml2', [
                          m('i.blue' + (summaryEligible ? '.pointer' : '.o-40') + '.' + (hidden ? 'ion-ios-circle-outline' : 'ion-ios-circle-filled'), {
                            title: summaryEligible ? 'Toggle dataset visibility' : 'Dataset is not set to show in summary',
                            onclick: e => {
                              e.stopPropagation();
                              if (!summaryEligible) return;
                              ctrl.toggleHideHeader(dataset);
                            }
                          })
                        ])
                      ]),
                      
                    ]
                  );
                } else {
                  attrs.class = 'tc v-btm fw2 bw1 ph1 br nowrap';
                  return m('th', attrs, cell.content);
                }
              })
            )
          } else if (rowIdx === 1) {
            const fixedNameColumn = hasNameColumnWidth(ctrl.element());
            return m('tr', [
              m('th.v-btm.bb.f1.fw1.tl.bw1.pr3' + (fixedNameColumn ? '.smart-table-name-column' : '.nowrap'), [
                m('div.f1.bw1.flex.justify-between.items-centernarrow.grid-item-drag-handle.pv0', {style:{cursor: 'grab'}}, [
                  (function () {
                    const tableName = vnode.attrs.element().name;
                    const hasName = typeof tableName === 'string' ? tableName.trim().length > 0 : !!tableName;

                    return hasName
                      ? m('span.blue' + (fixedNameColumn ? '.smart-table-title-content' : '.nowrap'), {
                        title: tableName,
                        style: { display: fixedNameColumn ? 'block' : '' },
                        onclick: elementForm.editDialogue.bind(elementForm, vnode.attrs.element() , {delete:'true', move:'true', submitCallback: ctrl.parentReinitialize, dashboardCallback: ctrl.dashboardCallback})
                      }, tableName)
                      : m('i.ion-arrow-move.blue.f3', {
                        onclick: elementForm.editDialogue.bind(elementForm, vnode.attrs.element() , {delete:'true', move:'true', submitCallback: ctrl.parentReinitialize, dashboardCallback: ctrl.dashboardCallback}),
                        title: 'Edit Smart Table'
                      });
                  })()
                ])
              ]),
              ctrl.hasRoomColumn() ?
                m('th.v-btm.bb.f1.fw1.tl.bw1.pr3', ROOM_COLUMN_LABEL)
              :
                undefined,
              
              ...ctrl.stackedHeaderStructure().map(({ dataset }) => {
                return m('th.bb.bl-0.p-0', { style: { verticalAlign: 'top' } }, [
                  m(Sortable, {
                    onsort: (newOrder) => {
                      ctrl.reorderPoints(dataset, newOrder);
                    },
                    draggable: '.grid-table-point',
                    handleClass: '.grid-table-point-handle',
                    htmlElement: 'div',  // Each point is a div
                  }, 
                    dataset.points.map(point => {
                      const summaryEligible = isDatasetShowInSummary(dataset);
                      const ptHidden = !summaryEligible || (ctrl.isSummaryEnabled() && !ctrl.isPointVisible(dataset, point.name));
                      return m('div.bw1.br.grid-table-point.grid-table-point-handle', { 
                        key: point.name,
                        id: point.name,
                      }, [
                        m('div.tc.v-btm.fw2.nowrap.flex.justify-between', [
                          point.name,
                          m('div.ml2', [
                            m('i.blue' + (summaryEligible ? '.pointer' : '.o-40') + '.' + (ptHidden ? 'ion-ios-circle-outline' : 'ion-ios-circle-filled'), {
                              title: summaryEligible ? 'Toggle point visibility' : 'Dataset is not set to show in summary',
                              onclick: e => {
                                e.stopPropagation();
                                if (!summaryEligible) return;
                                ctrl.toggleHidePoint(dataset, point.name);
                              }
                            })
                          ])
                        ])
                      ])
                    })
                  )
                ])
              })
              
            ])
          }
        })
      );
    }

    /**
     * Renders the table body (tbody) for the equipment table.
     *
     * @returns {VNode} - The virtual DOM node representing the table body.
     */
    function renderTableBody() {
      // Build the array of row VNodes once
      const rows = ctrl.equipment().map(equipmentRecord =>
        renderTableRow(equipmentRecord)
      );
    
      // In edit mode, wrap tbody content in Sortable only when all rows fit on one page.
      if (edit.vm.editing() && ctrl.canUseManualRowOrder()) {
        return m(Sortable, {
          onsort: ctrl.reorderRows,
          draggable: '.grid-row-draggable',       // class applied on each <tr>
          handleClass: '.grid-row-drag-handle',   // a handle inside the first cell
          htmlElement: 'tbody',
        }, rows);
      }
    
      // Browse mode: plain tbody
      return m('tbody', rows);
    }

    /**
     * Renders an individual row in the table body.
     *
     * @param {Object} equipmentRecord - The equipment record object.
     * @returns {VNode} - The virtual DOM node representing the table row.
     */
    function renderTableRow(equipmentRecord) {
      const rowReorderEnabled = edit.vm.editing() && ctrl.canUseManualRowOrder();
      const attrs = {
        key: equipmentRecord['@rid'],
        id:  equipmentRecord['@rid'], // used by Sortable -> ctrl.reorderRows
        class: 'grid-table' + (rowReorderEnabled ? ' grid-row-draggable' : '')
      };

      const cells = [
        renderPathViewerCell(equipmentRecord)
      ];

      const locationCell = renderLocationNameCell(equipmentRecord);
      if (locationCell) cells.push(locationCell);

      cells.push(...renderMergedColumns(equipmentRecord));
      cells.push(renderEmptyCell());

      return m('tr.grid-table', attrs, cells);
    }

    /**
     * Renders the first cell with the path viewer.
     *
     * @param {Object} equipmentRecord - The equipment record object.
     * @returns {VNode} - The virtual DOM node representing the path viewer cell.
     */
    function renderPathViewerCell(equipmentRecord) {
      const fixedNameColumn = hasNameColumnWidth(ctrl.element());
      const rowReorderEnabled = edit.vm.editing() && ctrl.canUseManualRowOrder();
      const pathViewerContent = m(pathViewer, {
        equipmentRecord: equipmentRecord,
        parentRecord: ctrl.element().location || ctrl.element,
        groupByLocation: true,
        recordType: getEntityRecordType(equipmentRecord),
        truncateStart: fixedNameColumn
      });

      return m('td.bb.moon-gray.tl.nowrap.f4.pr1' + (fixedNameColumn ? '.overflow-hidden.smart-table-name-column' : ''), { key: 'name' }, [
        m('div.flex.items-center.justify-between', [
          m('div.flex.items-center' + (rowReorderEnabled ? '.grid-row-drag-handle' : '') + (fixedNameColumn ? '.smart-table-name-column-content' : ''), {
            title: getPathViewerTitle(equipmentRecord)
          }, [
            fixedNameColumn ? m('span.smart-table-name-path', [pathViewerContent]) : pathViewerContent
          ]),
          m('div.ml3.flex-none', [
            m(smartTableLinksCell, {
              inline: true,
              compact: true,
              equipmentRecord,
              elementRid: ctrl.element()['@rid'],
              submitCallback: ctrl.reinitializeSmartTable,
            })
          ])
        ]),
      ]);
    }

    function getPathViewerTitle(equipmentRecord) {
      return (equipmentRecord.$path || [])
        .filter(path => path && path.name)
        .map(path => path.name)
        .join(' / ');
    }

    function getNameColumnWidth(elementRecord) {
      const rawWidth = elementRecord && elementRecord.nameColumnWidth;
      const numericWidth = Number(rawWidth);
      if (!Number.isFinite(numericWidth) || numericWidth <= 0) return null;
      return Math.min(40, Math.max(8, numericWidth)) + 'rem';
    }

    function hasNameColumnWidth(elementRecord) {
      return getNameColumnWidth(elementRecord) !== null;
    }

    function getEntityRecordType(record) {
      if (!record) return 'equipment';
      var className = record['@class'];
      var equipmentMap = equipmentModel.equipmentClassTree && equipmentModel.equipmentClassTree.equipment;
      if (equipmentMap && className && equipmentMap[className]) return 'equipment';
      return 'location';
    }
    /**
     * Renders the location name cell.
     *
     * @param {Object} equipmentRecord - The equipment record object.
     * @returns {VNode} - The virtual DOM node representing the location name cell.
     */
    function renderLocationNameCell(equipmentRecord) {
      if (!ctrl.hasRoomColumn()) return;

      const locationNumbers = getLinkedRoomLabels(equipmentRecord).join(', ');

      return m('td.bb.bl-0.f5.nowrap', { key: 'location' }, locationNumbers || 'N/A');
    }


    /**
     * Renders the merged columns for a given equipment record.
     *
     * @param {Object} equipmentRecord - The equipment record object.
     * @param {string} link - The URL link for the equipment record.
     * @returns {Array<VNode>} - An array of virtual DOM nodes representing the merged columns.
     */
    function stableKeyPart(value, fallback) {
      return value === undefined || value === null ? fallback : String(value);
    }

    function getPointCellKey(equipmentRecord, datasetHeader, point, instance, columnRecord) {
      const columnRid = columnRecord && columnRecord['@rid'];
      const pointRid = columnRecord && columnRecord.in && columnRecord.in['@rid'];
      const pointId = point && (
        point.id !== undefined ? point.id :
        point.index !== undefined ? point.index :
        point.name
      );

      return [
        'point',
        stableKeyPart(equipmentRecord && equipmentRecord['@rid'], 'record'),
        stableKeyPart(ctrl.getDatasetKey(datasetHeader), 'dataset'),
        stableKeyPart(pointId, 'point'),
        stableKeyPart(instance, 'self'),
        stableKeyPart(columnRid, 'no-column'),
        stableKeyPart(pointRid, 'no-point')
      ].join('::');
    }

    function renderMergedColumns(equipmentRecord) {
      return getVisibleDatasetsForCells().reduce((cells, datasetHeader) => {
        if (edit.vm.editing()) {
          cells.push(m('td.bb.bl-0', {
            key: [
              'dataset-edit',
              stableKeyPart(equipmentRecord && equipmentRecord['@rid'], 'record'),
              stableKeyPart(ctrl.getDatasetKey(datasetHeader), 'dataset')
            ].join('::')
          }));
          return cells;
        }

        function addPointCellForInstance(point, instance) {
          const columnRecord = findColumnRecord(equipmentRecord, datasetHeader, point, instance);
          return renderMergedPointCell(
            columnRecord,
            datasetHeader,
            getPointCellKey(equipmentRecord, datasetHeader, point, instance, columnRecord)
          );
        }

        getVisiblePointsForDataset(datasetHeader).forEach(point => {
          if (datasetHeader.instances.length === 0) {
            cells.push(addPointCellForInstance(point));
          } else {
            datasetHeader.instances.forEach(instance => {
              cells.push(addPointCellForInstance(point, instance));
            });
          }
        });
        return cells;
      }, []);
    }

    function getVisibleDatasetsForCells() {
      let datasets = ctrl.equipmentClassHierarchy();
      if (!edit.vm.editing() && ctrl.isSummaryEnabled()) {
        datasets = datasets.filter(ds => ctrl.isDatasetVisible(ds));
      }
      return datasets;
    }

    function getVisiblePointsForDataset(datasetHeader) {
      let pointsToRender = datasetHeader.points;
      if (!edit.vm.editing() && ctrl.isSummaryEnabled()) {
        const key = ctrl.getDatasetKey(datasetHeader);
        const pointsByDataset = ctrl.visiblePoints();
        if (Object.prototype.hasOwnProperty.call(pointsByDataset, key)) {
          pointsToRender = pointsToRender.filter(point => pointsByDataset[key].has(point.name));
        }
      }
      return pointsToRender;
    }

    function getVisibleLeafColumnDescriptors() {
      if (edit.vm.editing()) return [];
      return getVisibleDatasetsForCells().reduce((descriptors, datasetHeader) => {
        getVisiblePointsForDataset(datasetHeader).forEach(point => {
          if (datasetHeader.instances.length === 0) {
            descriptors.push({ datasetHeader, point });
          } else {
            datasetHeader.instances.forEach(instance => {
              descriptors.push({ datasetHeader, point, instance });
            });
          }
        });
        return descriptors;
      }, []);
    }

    function massWriteTitle(descriptor) {
      const datasetLabel = descriptor.datasetHeader && descriptor.datasetHeader.label;
      const pieces = datasetLabel && datasetLabel !== DEFAULT_DATASET_NAME ? [datasetLabel] : [];
      if (descriptor.point && descriptor.point.name) pieces.push(descriptor.point.name);
      if (descriptor.instance) pieces.push(descriptor.instance);
      return 'Mass Write: ' + pieces.filter(Boolean).join(' / ');
    }

    function massOverrideTitle(descriptor) {
      const datasetLabel = descriptor.datasetHeader && descriptor.datasetHeader.label;
      const pieces = datasetLabel && datasetLabel !== DEFAULT_DATASET_NAME ? [datasetLabel] : [];
      if (descriptor.point && descriptor.point.name) pieces.push(descriptor.point.name);
      if (descriptor.instance) pieces.push(descriptor.instance);
      return 'Mass Override: ' + pieces.filter(Boolean).join(' / ');
    }

    function pathRecordLabel(record) {
      if (!record) return '';
      if (record.name === 'root') return 'Home';
      if (record['@class'] === 'Room' && record.roomNumber) {
        return [record.name, record.roomNumber].filter(Boolean).join(' ');
      }
      return record.name || record.label || record['@class'] || record['@rid'] || '';
    }

    function massWritePointPath(equipmentRecord, descriptor, childRecord) {
      const parts = Array.isArray(equipmentRecord && equipmentRecord.$path)
        ? equipmentRecord.$path.map(pathRecordLabel).filter(Boolean)
        : [pathRecordLabel(equipmentRecord)].filter(Boolean);
      if (childRecord && childRecord.name && parts[parts.length - 1] !== childRecord.name) {
        parts.push(pathRecordLabel(childRecord));
      }
      if (descriptor.datasetHeader && descriptor.datasetHeader.label) {
        parts.push(descriptor.datasetHeader.label);
      }
      if (descriptor.point && descriptor.point.name) {
        parts.push(descriptor.point.name);
      }
      return parts.join(' / ');
    }

    function massWriteRowsForDescriptor(descriptor) {
      return ctrl.equipment().map(equipmentRecord => {
        const childRecord = descriptor.instance
          ? findChildEquipmentRecord(equipmentRecord, descriptor.datasetHeader.className, descriptor.instance)
          : null;
        const columnRecord = findColumnRecord(
          equipmentRecord,
          descriptor.datasetHeader,
          descriptor.point,
          descriptor.instance
        );
        if (!columnRecord || !columnRecord.in) return null;
        return {
          equipmentRecord,
          columnRecord: Object.assign({}, columnRecord, {
            permission: getPointCellPermission(columnRecord, descriptor.datasetHeader)
          }),
          path: massWritePointPath(equipmentRecord, descriptor, childRecord)
        };
      }).filter(Boolean);
    }

    function pointSupportsWrite(pointRecord) {
      return !!(pointRecord && isPermissionEnabled(pointRecord.writable));
    }

    function pointSupportsOverride(pointRecord) {
      return !!(pointRecord && isPermissionEnabled(pointRecord.overrides));
    }

    function descriptorHasWritablePoint(descriptor) {
      return massWriteRowsForDescriptor(descriptor).some(row => {
        return row && row.columnRecord &&
          pointSupportsWrite(row.columnRecord.in) &&
          canWriteColumn(row.columnRecord);
      });
    }

    function descriptorHasOverridePoint(descriptor) {
      return massWriteRowsForDescriptor(descriptor).some(row => {
        return row && row.columnRecord &&
          pointSupportsOverride(row.columnRecord.in) &&
          canWriteColumn(row.columnRecord);
      });
    }

    function openMassWriteModal(descriptor) {
      const rows = massWriteRowsForDescriptor(descriptor);
      if (rows.length === 0) return;
      massPointWriteModal.editDialogue({
        title: massWriteTitle(descriptor) + ' (' + rows.length + ')',
        parentRid: getPointWriteParentRid(),
        rows
      });
    }

    function openMassOverrideModal(descriptor) {
      const rows = massWriteRowsForDescriptor(descriptor);
      if (rows.length === 0) return;
      massPointOverrideModal.editDialogue({
        title: massOverrideTitle(descriptor) + ' (' + rows.length + ')',
        parentRid: getPointWriteParentRid(),
        rows
      });
    }

    /**
     * Finds the column record for a given point.
     *
     * @param {Object} equipmentRecord - The equipment record object.
     * @param {Object} equipmentClassHeader - The equipment class header object.
     * @param {Object} dataset - The dataset object.
     * @param {Object} point - The point object within the dataset.
     * @returns {Object|null} - The found column record, or null if not found.
     */
    function findColumnRecord(equipmentRecord, equipmentClassHeader, point, instance) {
      // 1) Check if this record itself is the direct match
      if (!instance && (equipmentRecord['@class'] === equipmentClassHeader.className || ctrl.element().equipmentClasses.includes(equipmentClassHeader.className))) {
        return findColumnInRecord(equipmentRecord, equipmentClassHeader.datasetName, point);
      }

      // 2) Otherwise look for a matching child instance
      if (instance && equipmentRecord.out_hasComponent) {
        const childRecord = findChildEquipmentRecord(
          equipmentRecord,
          equipmentClassHeader.className,
          instance
        );
        if (childRecord) {
          return findColumnInRecord(childRecord, equipmentClassHeader.datasetName, point);
        }
      }

      // No match found
      return null;
    }

    /**
     * Renders a merged point cell for a given point.
     *
     * @param {Object|null} columnRecord - The column record object, or null if not found.
     * @param {Object} dataset - The dataset object.
     * @returns {VNode} - The virtual DOM node representing the point cell.
     */
    function isPermissionEnabled(value) {
      return value === true || value === 1 || value === '1';
    }

    function getDatasetPermissionKeys(dataset) {
      const keys = [];
      const className = dataset && dataset.className;
      if (className) keys.push(className);

      const classTree = equipmentModel.equipmentClassTree || {};
      const categoryName = classTree.equipment && classTree.equipment[className];
      if (categoryName && keys.indexOf(categoryName) === -1) {
        keys.push(categoryName);
      }

      return keys;
    }

    function getClassPermission(tablePermission, dataset) {
      const classPermissions = tablePermission && tablePermission.classPermissions;
      if (!classPermissions || typeof classPermissions !== 'object') return null;

      const keys = getDatasetPermissionKeys(dataset);
      for (let i = 0; i < keys.length; i++) {
        if (Object.prototype.hasOwnProperty.call(classPermissions, keys[i])) {
          return classPermissions[keys[i]];
        }
      }

      return { read: 0, write: 0 };
    }

    function getPointCellPermission(columnRecord, dataset) {
      const tablePermission = ctrl.element().permission || {};
      const columnPermission = columnRecord && columnRecord.permission;
      const permission = Object.assign({}, tablePermission, columnPermission || {});
      const classPermission = getClassPermission(tablePermission, dataset);

      if (classPermission) {
        permission.read = (isPermissionEnabled(classPermission.read) || isPermissionEnabled(classPermission.write)) ? 1 : 0;
        permission.write = isPermissionEnabled(classPermission.write) ? 1 : 0;
      }

      return permission;
    }

    function renderMergedPointCell(columnRecord, dataset, key) {
      
      if (columnRecord && columnRecord.in) {
        const writableColumnRecord = Object.assign({}, columnRecord, {
          permission: getPointCellPermission(columnRecord, dataset)
        });

        return m(pointTd, {
          key: key,
          columnRecord: writableColumnRecord,
          parentReinitialize: ctrl.parentReinitialize,
          parentRid: getPointWriteParentRid(),
        });
      } else {
        return renderEmptyOrEditCell(dataset, key);
      }
    }

    function getPointWriteParentRid() {
      const elementRecord = ctrl.element();
      const locationRecord = elementRecord.location && elementRecord.location();
      return locationRecord && locationRecord['@class'] === 'Dashboard'
        ? elementRecord.parentRid
        : elementRecord['@rid'];
    }

    /**
     * Renders an empty cell or an edit cell depending on the edit mode.
     *
     * @param {Object} dataset - The dataset object.
     * @param {string} link - The URL link for the equipment record.
     * @returns {VNode} - The virtual DOM node representing the empty or edit cell.
     */
    function renderEmptyOrEditCell(dataset, key) {
      return m('td.bb.bl-0', { key: key });
    }

    /**
     * Renders an empty cell for the last column in the row.
     *
     * @returns {VNode} - The virtual DOM node representing the empty cell.
     */
    function renderEmptyCell() {
      return m('td.tc.v-btm.bb.fw2.pv1', { key: 'fill', style: { width: '99%' } });
    }


    /**
     * Finds a child equipment record by name and class.
     *
     * @param {Object} equipmentRecord - The parent equipment record.
     * @param {string} className - The class name to match.
     * @param {string} equipmentName - The name of the equipment.
     * @returns {Object|null} - The child equipment record, or null if not found.
     */
    function findChildEquipmentRecord(equipmentRecord, className, equipmentName) {

      const edges = equipmentRecord.out_hasComponent || [];

      // 1) exact match (legacy)
      let edge = edges.find(e => {
        const c = e.in;
        return c && c.name === equipmentName && c['@class'] === className;
      });
      if (edge) return edge.in;

      // 2) suffix match (collapsed A/B/C)
      const suffixMatches = edges.filter(e => {
        const c = e.in;
        if (!c || !c.name || c['@class'] !== className) return false;
        if (typeof c.name !== 'string') return false;
        const parts = c.name.split(/[- ]/);
        return parts[parts.length - 1] === equipmentName;
      });
      if (suffixMatches.length === 1) {
        return suffixMatches[0].in;
      }

      // 3) if multiple, pick the one whose prefix is this row’s equipmentRecord.name
      if (suffixMatches.length > 1 && equipmentRecord.name) {
        const prefix = typeof equipmentRecord.name === 'string' ? equipmentRecord.name : String(equipmentRecord.name);
        edge = suffixMatches.find(e => typeof e.in.name === 'string' && e.in.name === `${prefix}-${equipmentName}` && e.in['@class'] === className);
        if (edge) return edge.in;
        // fallback to the first one
        return suffixMatches[0].in;
      }

      // nothing matched
      return null;
    }

    /**
     * Finds the column record in a given equipment record and dataset.
     *
     * @param {Object} equipmentRecord - The equipment record object.
     * @param {Object} dataset - The dataset object.
     * @param {string} column - The name of the point to match.
     * @returns {Object|null} - The column record, or null if not found.
     */
    function findColumnInRecord(equipmentRecord, datasetName, column) {
      let columnRecord = null;

      (equipmentRecord.out_Contains || []).forEach(containsEdge => {
        const datasetRecord = containsEdge.in;
        if (datasetRecord.out_Column && datasetRecord.name === datasetName) {
          datasetRecord.out_Column.forEach(columnEdge => {
            if (columnEdge.name == column.name) {
              columnRecord = columnEdge;
            }
          });
        }
      });

      return columnRecord;
    }

    function renderServerPagination() {
      const numberOfPages = ctrl.pageCount();
      const selectedPage = ctrl.currentPage();
      if (numberOfPages <= 1) return m('div');
      const totalKnown = ctrl.totalEquipmentCountKnown();
      const pageCountLabel = totalKnown
        ? String(numberOfPages)
        : (ctrl.hasMorePages() ? (numberOfPages + '+') : String(numberOfPages));

      const list = function (page) {
        return m('a.f4.ba.bw1.ph3.pv2.tc.v-btm.dib.pointer.ml2.mb1' + (selectedPage === page ? ' bg-mid-gray' : ''), {
          onclick: function () {
            ctrl.goToPage(page);
          }
        }, [
          m('span', {'aria-hidden': 'true'}, page),
        ]);
      };

      const tabs = [];
      if (numberOfPages <= 7) {
        for (let i = 1; i <= numberOfPages; i++) {
          tabs.push(list(i));
        }
      } else if (selectedPage < 4) {
        for (let i = 1; i <= 7; i++) {
          tabs.push(list(i));
        }
      } else if (selectedPage > 3 && (numberOfPages - 2) > selectedPage) {
        for (let i = selectedPage - 3; i <= selectedPage + 3; i++) {
          tabs.push(list(i));
        }
      } else {
        for (let i = numberOfPages - 6; i <= numberOfPages; i++) {
          tabs.push(list(i));
        }
      }

      return m('div.flex.flex-wrap', {
        id: 'pagination-container-' + ctrl.element()['@rid']
      }, [
        m('a.f4.ba.bw1.ph3.pv2.tc.v-btm.dib.pointer.mb1', {
          onclick: function () {
            if (selectedPage !== 1) {
              ctrl.goToPage(1);
            }
          },
        }, [
          m('span', {'aria-hidden': 'true'}, '«'),
        ]),
        m('a.f4.ba.bw1.ph3.pv2.tc.v-btm.dib.pointer.ml2.mb1', {
          onclick: function () {
            if (selectedPage !== 1) {
              ctrl.goToPage(selectedPage - 1);
            }
          }
        }, [
          m('span', {'aria-hidden': 'true'}, '<'),
        ]),
        tabs,
        m('a.f4.ba.bw1.ph3.pv2.tc.v-btm.dib.pointer.ml2.mb1', {
          onclick: function () {
            if (selectedPage !== numberOfPages) {
              ctrl.goToPage(selectedPage + 1);
            }
          }
        }, [
          m('span', {'aria-hidden': 'true'}, '>'),
        ]),
        m('a.f4.ba.bw1.ph3.pv2.tc.v-btm.dib.pointer.ml2.mb1', {
          onclick: function () {
            if (selectedPage !== numberOfPages) {
              ctrl.goToPage(numberOfPages);
            }
          }
        }, [
          m('span', {'aria-hidden': 'true'}, '»'),
        ]),
        m('a.f4.bw1.ph3.pv2.tc.v-btm.dib.pointer.ml2.mb1.fr', [
          m('span.nowrap', {'aria-hidden': 'true'}, selectedPage + ' of ' + pageCountLabel),
        ]),
      ]);
    }

    function renderPagination() {
      if (ctrl.usesServerPagination()) {
        return renderServerPagination();
      }

      return m(pagination, {
        data: ctrl.equipment(),
        itemsPerPage: Number(ctrl.element().rowLimit) || 10,
        searchEquipmentValue: ctrl.searchEquipmentValue,
      });
    }

    if (!ctrl.showTable()) { return; }
    const hostId = 'smart-table-links-host-' + ctrl.element()['@rid'];
    const nameColumnWidth = getNameColumnWidth(ctrl.element());
    const smartTableStyle = nameColumnWidth
      ? { '--smart-table-name-column-width': nameColumnWidth }
      : {};
    return m('div', {
      id: 'smart-table-container-' + vnode.attrs.element()['@rid'],
      style: smartTableStyle,
    },[
      m('div', { id: hostId, style: { position: 'relative', overflow: 'visible' } }),
      m('div.cf.overflow-hidden',[
        m('div.dragscroll-container',[
          (()=>{
            return m('div.dragscroll', {
              oncreate: () => {
                if (edit.vm.editing()) {
                Dragscroll();
              } else {
                Dragscroll(0, document.querySelector('.dragscroll'));
              }
            }
          },[
            m('table.fw2.f3.pr4.pb3.narrow' + (ctrl.loading() ? '.skeleton' : ''), {
              style: {tableLayout: nameColumnWidth ? 'fixed' : 'auto'},
              'aria-busy': ctrl.loading() ? 'true' : 'false'
            }, [
              edit.vm.editing() ? renderEditModeHeader() : renderHeader(),
              renderTableBody()
            ])
          ])
        })(),
      ])
      
      ]),
      m('div.cf.mt1', [
        renderPagination()
      ])
    ])
  }
};

module.exports = smartTable;
